import { X } from "lucide-react";
import React from "react";

import type { Scenario } from "@/data/feasibilityScenarios";

interface ScenarioSidebarProps {
  scenario: Scenario | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ScenarioSidebar: React.FC<ScenarioSidebarProps> = ({
  scenario,
  isOpen,
  onClose,
}) => {
  // Don't render if no scenario selected
  if (!isOpen || !scenario) return null;

  return (
    <>
      {/* Mobile backdrop overlay */}
      <div
        className={`
          lg:hidden
          fixed
          inset-0
          bg-black/50
          z-40
          transition-opacity
          duration-300
          ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
        `}
        onClick={onClose}
      />
      <div
        className={`
        fixed lg:absolute
        right-0
        top-0
        bottom-0
        bg-white
        border-l-2
        border-slate-200
        shadow-2xl
        transition-all
        duration-300
        overflow-hidden
        z-50
        ${isOpen ? "w-full sm:w-[400px] lg:w-[480px]" : "w-0"}
      `}
      >
        <div className="h-full w-full overflow-auto">
          <div className="p-5 sm:p-6 lg:p-8">
            {/* Header with Close Button */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-[#B87333] text-white rounded-xl">
                    {scenario.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                      {scenario.name}
                    </h2>
                    <p className="text-sm text-slate-600">
                      Real-world case study
                      <span className="text-red-500">*</span>
                    </p>
                    <p className="text-xs text-red-500">
                      *Company and personnel details have been fictionalized for
                      privacy
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  aria-label="Close sidebar"
                >
                  <X className="h-6 w-6 text-slate-600" />
                </button>
              </div>
            </div>

            {/* Company Profile */}
            <div className="mb-6 bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                Company Profile
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-semibold text-slate-700">Company:</span>{" "}
                  <span className="text-slate-600">
                    {scenario.company.name}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">
                    Industry:
                  </span>{" "}
                  <span className="text-slate-600">
                    {scenario.company.industry}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Size:</span>{" "}
                  <span className="text-slate-600">
                    {scenario.company.size}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Revenue:</span>{" "}
                  <span className="text-slate-600">
                    {scenario.company.revenue}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Founded:</span>{" "}
                  <span className="text-slate-600">
                    {scenario.company.founded}
                  </span>
                </div>
                <p className="text-slate-600 leading-relaxed mt-3">
                  {scenario.company.description}
                </p>
              </div>
            </div>

            {/* Decision Maker */}
            <div className="mb-6 bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                Decision Maker
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-slate-700">Name:</span>{" "}
                  <span className="text-slate-600">
                    {scenario.decisionMaker.name}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Role:</span>{" "}
                  <span className="text-slate-600">
                    {scenario.decisionMaker.role}
                  </span>
                </div>
                <p className="text-slate-600 leading-relaxed mt-3">
                  {scenario.decisionMaker.background}
                </p>
              </div>
            </div>

            {/* The Story */}
            <div className="space-y-6">
              {/* Situation */}
              <div className="bg-white rounded-xl p-6 border-2 border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  📋 The Situation
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  {scenario.story.situation}
                </p>
              </div>

              {/* Thought Process */}
              <div className="bg-amber-50 rounded-xl p-6 border-2 border-amber-200">
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  💭 Thought Process
                </h3>
                <p className="text-slate-700 leading-relaxed italic">
                  {scenario.story.thoughtProcess}
                </p>
              </div>

              {/* What They Decided */}
              <div className="bg-purple-50 rounded-xl p-6 border-2 border-purple-200">
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  ⚖️ What They Decided
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  {scenario.story.whatTheyDecided}
                </p>
              </div>

              {/* Assessing Correctly */}
              <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  ✅ Assessing Correctly
                </h3>
                <ul className="space-y-2">
                  {scenario.story.assessingCorrectly.map((item, idx) => (
                    <li
                      key={idx}
                      className="text-sm text-slate-700 leading-relaxed flex gap-2"
                    >
                      <span className="text-green-600 font-bold">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Potentially Missing */}
              <div className="bg-orange-50 rounded-xl p-6 border-2 border-orange-200">
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  ⚠️ Potentially Missing
                </h3>
                <ul className="space-y-2">
                  {scenario.story.potentiallyMissing.map((item, idx) => (
                    <li
                      key={idx}
                      className="text-sm text-slate-700 leading-relaxed flex gap-2"
                    >
                      <span className="text-orange-600 font-bold">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Concerns */}
              <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  😰 Concerns
                </h3>
                <ul className="space-y-2">
                  {scenario.story.concerns.map((item, idx) => (
                    <li
                      key={idx}
                      className="text-sm text-slate-700 leading-relaxed flex gap-2"
                    >
                      <span className="text-red-600 font-bold">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What They Did */}
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  🔨 What They Did
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  {scenario.story.whatTheyDid}
                </p>
              </div>

              {/* Outcome */}
              <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl p-6 text-white">
                <h3 className="text-lg font-bold mb-3">📊 The Outcome</h3>
                <p className="leading-relaxed">{scenario.story.outcome}</p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 p-6 bg-gradient-to-r from-[#B87333] to-[#A66929] rounded-xl text-white text-center">
              <h3 className="font-bold text-xl mb-3">
                Learn From Their Experience
              </h3>
              <p className="mb-4 text-sm">
                See how your project compares and get personalized
                recommendations
              </p>
              <button
                onClick={() => {
                  window.open(
                    "https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7",
                    "_blank",
                  );
                }}
                className="bg-white text-[#B87333] px-6 py-3 rounded-lg font-bold hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Schedule Free Assessment →
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
